// Pure helpers of the "Afiliación" sidebar section (R65). No React and relative imports only: the web test
// runner compiles files one by one.
//
// The researcher's units are mirrored as personal-data rows (`unidad` for the principal, `unidad_adicional`
// for each additional one). Those rows stay the source of the displayed name (a renamed "Otra" included) and of
// the order, but they are no longer SHOWN in "Información personal": the section builds its list from them.
// A unit has no visibility: the researcher either belongs to it (and it is shown) or does not, like a research line.

import { esUnidadOtra } from '@siiv/schemas';
import type { HoverTipCard } from '../../layout/hoverTipCard';

/** `tipo_dato_personal_investigador.codigo` of the principal unit row. */
export const CODIGO_UNIDAD = 'unidad';
/** `tipo_dato_personal_investigador.codigo` of the rows that mirror the additional units. */
export const CODIGO_UNIDAD_ADICIONAL = 'unidad_adicional';

/** Whether a personal-data row mirrors a unit (it belongs to "Afiliación", not to "Información personal"). */
export function esFilaDeUnidad(codigo: string): boolean {
	return codigo === CODIGO_UNIDAD || codigo === CODIGO_UNIDAD_ADICIONAL;
}

/** The personal-data rows "Información personal" shows: everything but the unit mirror rows. */
export function sinFilasDeUnidad<T extends { codigo: string }>(filas: readonly T[]): T[] {
	return filas.filter((fila) => !esFilaDeUnidad(fila.codigo));
}

/** Key of a personal-data row (composite primary key): what the rename and order endpoints take. */
export interface FilaEspejo {
	idTipoDatoPersonal: number;
	secuencia: number;
}

/** A personal-data row, in the shape both the public profile and the edit state can be mapped to. */
export interface FilaDeUnidad {
	codigo: string;
	/** Displayed text of the row (the unit name, or the custom name of "Otra"). */
	valor: string;
	/** Unit the row mirrors (`valor_json.idUnidad`); only the owner surfaces carry it. */
	idUnidad?: number | null;
	idTipoDatoPersonal?: number;
	secuencia?: number;
	/** Saved order (`orden_visual`): it orders the additional units. Absent on a source that does not carry it. */
	ordenVisual?: number;
	/** Macrounidad (faculty) of the unit the row mirrors; absent/null for a unit without one ("Otra"). */
	macrounidad?: string | null;
}

export interface UnidadAfiliada {
	/** Stable, unique within the list: React key. */
	clave: string;
	nombre: string;
	principal: boolean;
	/** The generic "Otra" unit: the only one whose displayed name can be customised. */
	esOtra: boolean;
	/** Faculty (macrounidad) of the unit, for the tooltip; null when the unit has none or the source does not say. */
	facultad: string | null;
}

export interface OpcionesAfiliacion {
	/** Name of the principal unit when no usable `unidad` row carries it (the investigator's own unit). */
	unidadPrincipal?: string | null;
	/** Id of the principal unit, to tell "Otra" when its row has no id. */
	idUnidadPrincipal?: number | null;
}

function claveDeFila(fila: FilaDeUnidad, posicion: number): string {
	return typeof fila.idTipoDatoPersonal === 'number' && typeof fila.secuencia === 'number'
		? `adicional:${fila.idTipoDatoPersonal}:${fila.secuencia}`
		: `adicional#${posicion}`;
}

const limpiar = (texto: string | null | undefined): string | null => texto?.trim() || null;

/**
 * The units the "Afiliación" section lists: the principal first, then the additional ones in their SAVED order
 * (`ordenVisual` of their mirror rows; the arrival order when the rows do not all carry it, and for equal values).
 * Every unit with a name is listed: there is no way to hide one. Future affiliation fields are appended to the
 * section's rows, not here: this list only says WHICH units there are.
 */
export function construirAfiliacion(filas: readonly FilaDeUnidad[], opciones: OpcionesAfiliacion = {}): UnidadAfiliada[] {
	const lista: UnidadAfiliada[] = [];

	const filaPrincipal = filas.find((fila) => fila.codigo === CODIGO_UNIDAD && fila.valor.trim().length > 0);
	const nombrePrincipal = filaPrincipal?.valor.trim() || opciones.unidadPrincipal?.trim() || '';
	if (nombrePrincipal) {
		lista.push({
			clave: 'principal',
			nombre: nombrePrincipal,
			principal: true,
			// The row id says which unit it mirrors; the investigator's own unit only decides when the row has none.
			esOtra: esUnidadOtra(filaPrincipal?.idUnidad ?? opciones.idUnidadPrincipal ?? null),
			facultad: limpiar(filaPrincipal?.macrounidad),
		});
	}

	const adicionales: { unidad: UnidadAfiliada; ordenVisual: number | undefined }[] = [];
	let posicion = 0;
	for (const fila of filas) {
		if (fila.codigo !== CODIGO_UNIDAD_ADICIONAL) continue;
		const nombre = fila.valor.trim();
		if (!nombre) continue;
		const clave = claveDeFila(fila, posicion);
		posicion += 1;
		adicionales.push({
			ordenVisual: fila.ordenVisual,
			unidad: {
				clave,
				nombre,
				principal: false,
				esOtra: esUnidadOtra(fila.idUnidad ?? null),
				facultad: limpiar(fila.macrounidad),
			},
		});
	}

	// Saved order only when EVERY additional row carries it (a mix has no consistent order); `sort` is stable, so
	// equal values keep the order the rows arrived in.
	if (adicionales.every((item) => typeof item.ordenVisual === 'number')) {
		adicionales.sort((a, b) => (a.ordenVisual as number) - (b.ordenVisual as number));
	}
	for (const item of adicionales) lista.push(item.unidad);

	return lista;
}

/** Profile personal-data fields (`PersonalDataField`) as unit rows: structural, so this file needs no app imports. */
export function filasDePerfil(
	personalData:
		| readonly {
				codigo: string;
				value: string;
				ordenVisual?: number;
				macrounidad?: string | null;
		  }[]
		| undefined,
): FilaDeUnidad[] {
	return (personalData ?? []).map((dato) => ({
		codigo: dato.codigo,
		valor: dato.value,
		ordenVisual: dato.ordenVisual,
		macrounidad: dato.macrounidad,
	}));
}

/**
 * The units a profile page lists: the principal and the additional ones, in the saved order. `perfil` is the
 * investigator of the profile (`InvestigatorRecord`), structurally.
 */
export function unidadesDelPerfil(perfil: {
	unit: string;
	idUnidad?: number | null;
	personalData?: Parameters<typeof filasDePerfil>[0];
}): UnidadAfiliada[] {
	return construirAfiliacion(filasDePerfil(perfil.personalData), {
		unidadPrincipal: perfil.unit,
		idUnidadPrincipal: perfil.idUnidad ?? null,
	});
}

/**
 * The hover card of a unit row, same style as the line card: the full unit name as header and one row per fact:
 * TIPO ("Principal" / "Adicional") and FACULTAD (the unit's macrounidad). FACULTAD is left out only when the unit
 * really has none ("Otra", a centre without one); a centre that is its own macrounidad still shows it.
 */
export function tarjetaDeUnidad(unidad: Pick<UnidadAfiliada, 'nombre' | 'principal' | 'facultad'>): HoverTipCard {
	const facultad = limpiar(unidad.facultad);
	return {
		icon: 'building',
		title: unidad.nombre,
		// A unit name never wraps: neither the header nor the FACULTAD value (also a unit name).
		titleUnaLinea: true,
		rows: [
			{ icon: 'tag', label: 'Tipo', values: [{ text: unidad.principal ? 'Principal' : 'Adicional' }] },
			...(facultad ? [{ icon: 'landmark' as const, label: 'Facultad', values: [{ text: facultad, unaLinea: true }] }] : []),
		],
	};
}

/**
 * The same facts as the card, as `sr-only` text inside the row: the tooltip only exists while hovering, focusing
 * or tapping, and a crawler or a screen reader never does (SEO rule: content in the served HTML).
 */
export function textoOcultoDeUnidad(unidad: Pick<UnidadAfiliada, 'nombre' | 'principal' | 'facultad'>): string {
	const facultad = limpiar(unidad.facultad);
	return [
		unidad.principal ? 'Unidad principal.' : 'Unidad adicional.',
		facultad ? `Facultad: ${facultad}.` : '',
	]
		.filter(Boolean)
		.join(' ');
}
